import {
  Day,
  addDays,
  eachDayOfInterval,
  format,
  formatISO,
  getTime,
  isSameDay,
  isWithinInterval,
  nextDay,
  parseISO,
  previousDay,
} from 'date-fns';

import { dateFnsWeekDayOrder, weekDayOrder } from 'constants/weekDayOrder';

import {
  Assignments,
  Courses,
  Student_Assignment_Status_Enum,
  Student_Assignments,
  Student_Assignments_Insert_Input,
  Student_Courses,
  Students,
} from 'graphql/types/schema';

/**
 * Course days are days on which the course schedules assignments (typically Monday - Friday)
 * Vacation days are days have a vacation scheduled
 * Available days are Course days that are not Vacation days
 */

const MAX_STUDENTS_FOR_INDIVIDUAL_DISPLAY = 4;
export enum DayStatus {
  Course,
  Available,
}

type StudentCourse = Pick<
  Student_Courses,
  'id' | 'start' | 'daysOfTheWeek' | 'courseDayOffsets'
> & {
  // Course is optional here only because completed student courses
  // technically have null courses. But a completed student course
  // should never use this utility
  course?: {
    id: string;
  } | null;
  student: Pick<Students, 'id'>;
};

export type StudentAssignmentForUpdatingGradingStyle = Pick<
  Student_Assignments,
  'pointsEarned' | 'pointsAvailable'
>;

type StudentAssignment = Pick<Student_Assignments, 'id' | 'start' | 'end'>;

type StudentAssignmentForReschedule = Pick<
  Student_Assignments,
  'id' | 'start' | 'end' | 'freeForm' | 'courseDayOffset' | 'status'
> & {
  assignment: {
    course: Pick<Courses, 'id'>;
  };
};

type StudentAssignmentForRescheduleUsingAssignment =
  StudentAssignmentForReschedule & {
    assignment: Pick<Assignments, 'week' | 'day' | 'duration'>;
  };

type NewStudentAssignmentForAssignment = Pick<
  Student_Assignments_Insert_Input,
  'studentId' | 'studentCourseId' | 'end' | 'start' | 'status' | 'freeForm'
>;

export type CalendarDateScheduleResult =
  | { week: number; day: number; reason: null }
  | {
      week: null;
      day: null;
      reason:
        | 'not_course_day'
        | 'students_differ'
        | 'outside_range'
        | 'no_students';
    };

type NewStudentAssignmentForStudentCourse = Pick<
  Student_Assignments_Insert_Input,
  'studentId' | 'assignmentId' | 'end' | 'start' | 'status'
>;

export interface DateRange {
  from: Date;
  to: Date;
}

export interface DateRangeWithIgnoredCourses extends DateRange {
  ignoredCourses: string[];
}

interface StudentAssignmentDates {
  start: Date | null;
  end: Date | null;
}

export interface StudentDateInfo {
  studentId: string;
  studentName: string;
  date: Date | null;
}

export interface StudentDatesMap {
  [weekDay: string]: StudentDateInfo[];
}

interface StudentCourseForDateCalc {
  id: string;
  start: string;
  daysOfTheWeek: Record<string, boolean> | object;
  courseDayOffsets?: Record<number, number> | object | null;
  student: { id: string; name: string };
  course?: { id: string } | null;
}

/**
 * Returns the date for the nth day of the week after a date.
 * For example: The 2nd Wednesday after 05/25/2022
 *
 * @param startDay {Date} date to start jumping from
 * @param endDayOfWeekIndex {Day} date-fns Day index of the day of the week we're jumping to
 * @param weeksToJump {number} number of weeks to jump (the Nth in 5th Monday)
 * @param jumpingForward {boolean} true if we're jumping forward, false if we're jumping backwards in time
 * @return {Date} date object without time
 */
export function jumpWeeks(
  startDay: Date,
  endDayOfWeekIndex: Day,
  weeksToJump: number,
  jumpingForward: boolean
): Date {
  return Array.from({ length: weeksToJump }).reduce<Date>(
    (currentDay) =>
      jumpingForward
        ? nextDay(currentDay, endDayOfWeekIndex)
        : previousDay(currentDay, endDayOfWeekIndex),
    startDay
  );
}

/**
 * Returns date with the time component removed
 *
 * @param date {Date} date object to remove the time from
 * @return {Date} date object without time
 */
export function removeTimeFromDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Returns true if day is within a range in dateRanges (inclusive), otherwise false
 *
 * @param day {Date} the day to test inclusion
 * @param dateRanges {DateRange[]} list of date ranges in which to test for inclusion
 * @return {boolean} true if day is within a range in dateRanges (inclusive), otherwise false
 */
export function dayInRanges(day: Date, dateRanges: DateRange[]): boolean {
  const dayNoTime = removeTimeFromDate(day);
  return dateRanges.some((dateRange) =>
    isWithinInterval(dayNoTime, {
      start: removeTimeFromDate(dateRange.from),
      end: removeTimeFromDate(dateRange.to),
    })
  );
}

/**
 * Returns true if day is within a vacation range that blocks the specified course (inclusive), otherwise false
 * A vacation blocks a course if the course ID is NOT in the vacation's ignoredCourses list
 *
 * @param day {Date} the day to test inclusion
 * @param vacations {DateRangeWithIgnoredCourses[]} list of vacation ranges with ignored courses
 * @param courseId {string} the course ID to check against vacation blocks
 * @return {boolean} true if day is within a vacation range that blocks the course, otherwise false
 */
export function isVacationDayForCourse(
  day: Date,
  vacations: DateRangeWithIgnoredCourses[],
  courseId: string
): boolean {
  const dayNoTime = removeTimeFromDate(day);
  return vacations.some((vacation) => {
    const isInVacationRange = isWithinInterval(dayNoTime, {
      start: removeTimeFromDate(vacation.from),
      end: removeTimeFromDate(vacation.to),
    });

    if (isInVacationRange) {
      return !vacation.ignoredCourses.includes(courseId);
    }

    return false;
  });
}

/**
 * Returns an Array of days of the week on which student assignments may be assigned
 *
 * @param studentCourseDays {Record<string, boolean>} days of week on which student assignments may be assigned
 * @param orderedDays {Array<string>} optional ordered list of week days (defaults to Monday-first)
 * @return {Array<string>} an Array of days of the week on which student assignments may be assigned
 */
export function getValidDaysOfWeek(
  studentCourseDays: Record<string, boolean>,
  orderedDays: string[] = weekDayOrder
): Array<string> {
  return orderedDays.filter((dayOfWeek) => studentCourseDays[dayOfWeek]);
}

/**
 * Counts the number of days per week a course is based on the student course days data structure
 *
 * @param studentCourseDays {Record<string, boolean>} days of week on which student assignments may be assigned
 * @return {number} number of days per week for the course
 */
export function getNumCourseDaysPerWeekFromStudentCourseDays(
  studentCourseDays: Record<string, boolean>
): number {
  return Object.values(studentCourseDays).reduce(
    (acc, day) => (day ? acc + 1 : acc),
    0
  );
}

/**
 * Get day number in course based on the week day combo and number of days per week
 * (e.g. Week 2, Day 1 in a 5 days a week course is "day 6")
 * also see getWeekAndDayInCourse()
 *
 * @param week {number | null} week number in course
 * @param day {number | null} day number in course
 * @param numCourseDaysPerWeek {number} number of days per week in the course
 * @return {number} day number in course for the given week day combo
 */
export function getNumDayInCourse(
  numCourseDaysPerWeek: number,
  week?: number | null,
  day?: number | null
): number {
  return week && day ? day + (week - 1) * numCourseDaysPerWeek : 0;
}

/**
 * Return the specific week and day of numDayInCourse
 * Also see getNumDayInCourse()
 *
 * @param numCourseDaysPerWeek number of days per week in the course
 * @param numDayInCourse number of days into the course - 1 is the first day of course
 * @returns the week and day of a day number in a course
 */
export function getWeekAndDayInCourse(
  numCourseDaysPerWeek: number,
  numDayInCourse: number
): { week: number; day: number } {
  const week = Math.floor((numDayInCourse - 1) / numCourseDaysPerWeek) + 1;
  const day = numDayInCourse - (week - 1) * numCourseDaysPerWeek;

  return { week, day };
}

/**
 * Returns a sum of all the courseDayOffsets for a Student Course after a week and day
 *
 * @param week {number} course week to sum up to
 * @param day {number} course day to sum up to
 * @param studentCourseDayOffsets {Record<number, number>} student course courseDayOffsets to sum up
 * @return {number} sum of all the offsets
 */
export function calcTotalStudentCourseDayOffset(
  week: number,
  day: number,
  numCourseDaysPerWeek: number,
  studentCourseDayOffsets: Record<number, number>
): number {
  return Array.from(
    { length: getNumDayInCourse(numCourseDaysPerWeek, week, day) },
    (_, i) => i + 1
  ).reduce((acc, day) => {
    return (studentCourseDayOffsets[day] ?? 0) + acc;
  }, 0);
}

/**
 * Computes per-position offset changes for a shift reschedule of multiple
 * selected assignments so that all land on the target date.
 *
 * When multiple assignments are selected (e.g., Mon + Tue on a M-F course)
 * and shift-rescheduled to a new date (e.g., Wed), each successive position
 * needs a decreasing individual offset so that the cumulative effect places
 * every selected assignment on the same target date.
 *
 * The effective gap between positions accounts for existing cumulative offsets,
 * not just the natural course day spacing. This matters when assignments have
 * already been collapsed onto the same date by prior offsets — e.g., after a
 * multi-select shift, a subsequent group drag must recognize that the positions
 * are effectively co-located rather than naturally spaced apart.
 *
 * @param assignmentPositions - The week/day positions of the selected assignments
 * @param numCourseDaysPerWeek - Number of course days per week
 * @param courseDayOffsetAdjustment - The offset adjustment to apply to the earliest
 *   selected position (i.e., how many course days to shift)
 * @param originalStudentCourseDayOffsets - Existing per-day offsets that the new
 *   offsets should build on top of
 * @returns A record mapping each dayInCourse to its new absolute offset value
 */
export function calcShiftRescheduleOffsets(
  assignmentPositions: { week: number; day: number }[],
  numCourseDaysPerWeek: number,
  courseDayOffsetAdjustment: number,
  originalStudentCourseDayOffsets: Record<number, number>
): Record<number, number> {
  const positionsWithDayInCourse = Array.from(
    new Map(
      assignmentPositions.map((position) => {
        const dayInCourse = getNumDayInCourse(
          numCourseDaysPerWeek,
          position.week,
          position.day
        );
        return [dayInCourse, { ...position, dayInCourse }];
      })
    ).values()
  ).sort((a, b) => a.dayInCourse - b.dayInCourse);

  const earliestPosition = positionsWithDayInCourse[0];
  const earliestExistingCumulative = calcTotalStudentCourseDayOffset(
    earliestPosition.week,
    earliestPosition.day,
    numCourseDaysPerWeek,
    originalStudentCourseDayOffsets
  );

  return positionsWithDayInCourse.reduce<{
    offsets: Record<number, number>;
    cumulativeChange: number;
  }>(
    (acc, { week, day, dayInCourse }) => {
      const existingCumulative = calcTotalStudentCourseDayOffset(
        week,
        day,
        numCourseDaysPerWeek,
        originalStudentCourseDayOffsets
      );
      const effectiveGap =
        dayInCourse -
        earliestPosition.dayInCourse +
        existingCumulative -
        earliestExistingCumulative;

      const desiredCumulativeChange = courseDayOffsetAdjustment - effectiveGap;
      const individualChange = desiredCumulativeChange - acc.cumulativeChange;

      return {
        offsets: {
          ...acc.offsets,
          [dayInCourse]:
            (originalStudentCourseDayOffsets[dayInCourse] ?? 0) +
            individualChange,
        },
        cumulativeChange: acc.cumulativeChange + individualChange,
      };
    },
    { offsets: {}, cumulativeChange: 0 }
  ).offsets;
}

/**
 * Converts an inclusive course-day count into a shift offset.
 * When counting available days between two dates inclusively, the result
 * is off by one from the actual shift needed. This corrects for that:
 * positive counts subtract 1, negative counts add 1, zero stays zero.
 *
 * @param inclusiveDayCount the inclusive count of available course days between two dates
 * @returns the number of course days to shift, corrected for inclusive counting
 */
export function calcOffsetFromDayCount(inclusiveDayCount: number): number {
  if (inclusiveDayCount === 0) {
    return 0;
  }
  return inclusiveDayCount < 0 ? inclusiveDayCount + 1 : inclusiveDayCount - 1;
}

/**
 * Returns the number of "course", and "available" days within dateRange (inclusive)
 *
 * If DateRange is "reversed" ('to' < 'from') the returned numbers will be <= 0.
 * If DateRange is one day ('to' == 'from') the returned numbers will be <= 1
 *
 * @param dateRange {DateRange} range of dates in which to count day types, inclusive
 * @param studentCourseDays {Record<string, boolean>} days of week on which student assignments may be assigned
 * @param vacations {DateRangeWithIgnoredCourses[]} list of vacations
 * @param courseId {string} the course ID to check against vacation blocks
 * @return {{ [DayStatus.Available]: number; [DayStatus.Course]: number }}
 *         number of "course" and "available" days within dateRange
 */
export function getDayStatusCounts(
  dateRange: DateRange,
  studentCourseDays: Record<string, boolean>,
  vacations: DateRangeWithIgnoredCourses[] = [],
  courseId: string
): {
  [DayStatus.Available]: number;
  [DayStatus.Course]: number;
} {
  const dateRangeIsReversed = dateRange.from > dateRange.to ? true : false;
  dateRange = dateRangeIsReversed
    ? {
        from: removeTimeFromDate(dateRange.to),
        to: removeTimeFromDate(dateRange.from),
      }
    : {
        from: removeTimeFromDate(dateRange.from),
        to: removeTimeFromDate(dateRange.to),
      };

  const dayCounts = eachDayOfInterval({
    start: dateRange.from,
    end: dateRange.to,
  }).reduce(
    (counts, currentDay) => {
      const isCourseDay = studentCourseDays[format(currentDay, 'EEEE')];
      const isVacationDay = isVacationDayForCourse(
        currentDay,
        vacations,
        courseId
      );

      return {
        [DayStatus.Available]:
          counts[DayStatus.Available] + (isCourseDay && !isVacationDay ? 1 : 0),
        [DayStatus.Course]: counts[DayStatus.Course] + (isCourseDay ? 1 : 0),
      };
    },
    {
      [DayStatus.Available]: 0,
      [DayStatus.Course]: 0,
    }
  );

  if (dateRangeIsReversed) {
    return {
      [DayStatus.Available]: -dayCounts[DayStatus.Available],
      [DayStatus.Course]: -dayCounts[DayStatus.Course],
    };
  } else {
    return dayCounts;
  }
}

/**
 * Returns the number of course days that a student assignment is offset from its originally scheduled date
 *
 * Can be negative if the student assignment start date occurs before its originally scheduled date
 *
 * @param studentCourseStart {Date} date that the student course starts
 * @param studentCourseDays {Record<string, boolean>} days of the week on which student assignments may be assigned
 * @param studentVacations {DateRange[]} list of vacations for the student in the student assignment, which do not count as course days
 * @param studentCourseDayOffsets {Record<number, number>} courseDayOffsets for the student course
 * @param assignmentDay {number | null} day number in the course week that the assignment is scheduled (1-indexed)
 * @param assignmentWeek {number | null} course week number that the assignment is scheduled (1-indexed)
 * @param studentAssignmentStart {Date} date that the student assignment is scheduled
 * @return {number} number of course days that a student assignment is offset from its originally scheduled date
 */
export function calcCourseDayOffset(
  studentCourseStart: Date,
  studentCourseDays: Record<string, boolean>,
  studentVacations: DateRangeWithIgnoredCourses[],
  studentCourseDayOffsets: Record<number, number>,
  assignmentDay: number | null,
  assignmentWeek: number | null,
  studentAssignmentStart: Date,
  courseId: string
): number {
  const studentCourseDayOffset =
    assignmentWeek && assignmentDay
      ? calcTotalStudentCourseDayOffset(
          assignmentWeek,
          assignmentDay,
          getNumCourseDaysPerWeekFromStudentCourseDays(studentCourseDays),
          studentCourseDayOffsets
        )
      : 0;

  const originalStudentAssignmentStart = getStudentAssignmentDates(
    studentCourseStart,
    studentCourseDays,
    studentVacations,
    assignmentDay,
    assignmentWeek,
    0,
    courseId,
    studentCourseDayOffset
  ).start;

  if (!originalStudentAssignmentStart) {
    return 0;
  }

  const numCourseDaysInRange = getDayStatusCounts(
    {
      from: originalStudentAssignmentStart,
      to: studentAssignmentStart,
    },
    studentCourseDays,
    studentVacations,
    courseId
  )[DayStatus.Available];

  return calcOffsetFromDayCount(numCourseDaysInRange);
}

/**
 * For each assignment day number in the student course, calculate its calendar date,
 * and return a mapping of each valid calendar date to its day numbers in the student course
 *
 * It's possible that the same calendar day maps to 1+ assignment day numbers.
 * For example, with an offset map like { 3:-1, 4:-2, 5:-3}.
 *
 * @param studentCourseStart date that the student course starts
 * @param studentCourseDays days of the week on which student assignments may be assigned
 * @param numberOfWeeks number of weeks in the course
 * @param numberOfDaysPerWeek number of days in each week of the course
 * @param studentVacations list of student vacations
 * @param studentCourseDayOffsets courseDayOffsets for the student course
 * @returns map of each calendar date (as an ISO string) to its day numbers in the student course
 */
export function calcWeekDayMap(
  studentCourseStart: Date,
  studentCourseDays: Record<string, boolean>,
  numberOfWeeks: number,
  numberOfDaysPerWeek: number,
  studentVacations: DateRangeWithIgnoredCourses[],
  studentCourseDayOffsets: Record<number, number>,
  courseId: string
): Record<string, number[]> {
  const weekNums = Array.from({ length: numberOfWeeks }, (_, i) => i + 1);
  const dayNums = Array.from({ length: numberOfDaysPerWeek }, (_, i) => i + 1);

  const dateToDayMap = weekNums
    .flatMap((week) =>
      dayNums.map((day) => {
        return {
          week,
          day,
          numDayInCourse: getNumDayInCourse(numberOfDaysPerWeek, week, day),
        };
      })
    )
    .reduce((map, { week, day, numDayInCourse }) => {
      const dayInCourse = getStudentAssignmentDates(
        studentCourseStart,
        studentCourseDays,
        studentVacations,
        day,
        week,
        0,
        courseId,
        calcTotalStudentCourseDayOffset(
          week,
          day,
          numberOfDaysPerWeek,
          studentCourseDayOffsets
        )
      ).start;

      if (dayInCourse) {
        const calendarDate = formatISO(dayInCourse, { representation: 'date' });
        if (calendarDate in map) {
          map[calendarDate].push(numDayInCourse);
        } else {
          map[calendarDate] = [numDayInCourse];
        }
      }
      return map;
    }, {} as Record<string, number[]>);

  return dateToDayMap;
}

/**
 * Checks if a calendar date falls on a day of the week that is a course day
 *
 * @param calendarDate - The calendar date to check
 * @param studentCourseDays - Record<string, boolean> for which weekdays are course days
 * @returns true if the date's weekday is a course day for this student
 */
export function isValidCourseDay(
  calendarDate: Date,
  studentCourseDays: Record<string, boolean>
): boolean {
  const dayOfWeek = format(calendarDate, 'EEEE');
  return !!studentCourseDays[dayOfWeek];
}

/**
 * Returns the week/day for a calendar date given a student's course schedule.
 * Returns null if the date is not a valid course day or is outside the course range.
 *
 * @param calendarDate - The calendar date to look up
 * @param studentCourseStart - Student's course start date
 * @param studentCourseDays - Which weekdays the student has class
 * @param numberOfWeeks - Total weeks in course
 * @param numberOfDaysPerWeek - Days per week in course
 * @param studentVacations - Student's vacation ranges
 * @param studentCourseDayOffsets - Student's day offsets
 * @param courseId - The course ID
 * @returns { week, day } or null if date not found/invalid
 */
export function getWeekDayForCalendarDate(
  calendarDate: Date,
  studentCourseStart: Date,
  studentCourseDays: Record<string, boolean>,
  numberOfWeeks: number,
  numberOfDaysPerWeek: number,
  studentVacations: DateRangeWithIgnoredCourses[],
  studentCourseDayOffsets: Record<number, number>,
  courseId: string
): { week: number; day: number } | null {
  if (!isValidCourseDay(calendarDate, studentCourseDays)) {
    return null;
  }

  const weekDayMap = calcWeekDayMap(
    studentCourseStart,
    studentCourseDays,
    numberOfWeeks,
    numberOfDaysPerWeek,
    studentVacations,
    studentCourseDayOffsets,
    courseId
  );

  const calendarDateStr = formatISO(calendarDate, { representation: 'date' });
  const dayNumbers = weekDayMap[calendarDateStr];

  if (!dayNumbers?.length) {
    return null;
  }

  const { week, day } = getWeekAndDayInCourse(
    numberOfDaysPerWeek,
    dayNumbers[0]
  );
  return { week, day };
}

/**
 * Determines whether a calendar date maps to a unanimous week/day for all students,
 * or whether all students should get free-form assignments.
 *
 * @param calendarDate - The selected calendar date
 * @param studentCourses - Array of student courses with their schedules
 * @param studentVacationsPerStudent - Array of vacation arrays (aligned with studentCourses)
 * @param numberOfWeeks - Course total weeks
 * @param numberOfDaysPerWeek - Course days per week
 * @param courseId - The course ID
 * @returns CalendarDateScheduleResult with week/day if unanimous, or reason for failure
 */
export function getUnanimousWeekDayForCalendarDate(
  calendarDate: Date,
  studentCourses: StudentCourse[],
  studentVacationsPerStudent: DateRangeWithIgnoredCourses[][],
  numberOfWeeks: number,
  numberOfDaysPerWeek: number,
  courseId: string
): CalendarDateScheduleResult {
  if (studentCourses.length === 0) {
    return { week: null, day: null, reason: 'no_students' };
  }

  const results = studentCourses.map(
    (studentCourse, i): CalendarDateScheduleResult => {
      const studentVacations = studentVacationsPerStudent[i] ?? [];

      if (
        !isValidCourseDay(
          calendarDate,
          studentCourse.daysOfTheWeek as Record<string, boolean>
        )
      ) {
        return { week: null, day: null, reason: 'not_course_day' };
      }

      const weekDay = getWeekDayForCalendarDate(
        calendarDate,
        parseISO(studentCourse.start),
        studentCourse.daysOfTheWeek as Record<string, boolean>,
        numberOfWeeks,
        numberOfDaysPerWeek,
        studentVacations,
        (studentCourse.courseDayOffsets ?? {}) as Record<number, number>,
        courseId
      );

      if (weekDay === null) {
        return { week: null, day: null, reason: 'outside_range' };
      }

      return { week: weekDay.week, day: weekDay.day, reason: null };
    }
  );

  const firstResult = results[0];
  if (firstResult.reason !== null) {
    return firstResult;
  }

  const errorResult = results.find((r) => r.reason !== null);
  if (errorResult) {
    return errorResult;
  }

  const allUnanimous = results.every(
    (r) => r.week === firstResult.week && r.day === firstResult.day
  );

  if (!allUnanimous) {
    return { week: null, day: null, reason: 'students_differ' };
  }

  return { week: firstResult.week, day: firstResult.day, reason: null };
}

/**
 * Returns the nth day of specified status on or after startDay
 *
 * If n > 0  return the nth day of the specified status on or after startDay
 * If n is < 0, return the nth day of the specified status on or before startDay
 * If n === 0, return startDay
 * If countStartDay === false, don't count startDay is a valid day
 * Examples:
 * The next "course day" on or after dayX (may return dayX): use n === 1
 * The next "course day" after dayX: use n === 1, countStartDay === false
 * The previous "course day" on or before dayX (may return dayX): use n === 1
 * The the 3rd "vacation day" on or after dayX: use n === 3
 * Two "available days" on or before dayX: use n === -2
 *
 * @param nDaysAhead {number} indicates the nth day to look for - use n === 1 to find the next day of specified type
 * @param dayStatus {DayStatus} type of day to count - "available" days are non-vacation course days
 * @param startDay {Date} first day on which to start counting towards the nth day
 * @param studentCourseDays {Record<string, boolean>} days of week on which student assignments may be assigned
 * @param vacationDays {Date[]} flat list of vacation days
 * @param countStartDay {boolean} if true (default), startDay contributes to the count towards N days
 * @return {Date} nth day of specified status on or after startDay
 */
export function findNthDay(
  nDaysAhead: number,
  dayStatus: DayStatus,
  startDay: Date,
  studentCourseDays: Record<string, boolean>,
  vacationDays: Date[] = [],
  countStartDay: boolean = true
): Date {
  if (nDaysAhead === 0) {
    return startDay;
  }

  const lookingForward = nDaysAhead > 0;

  const courseDaysOfWeek = getValidDaysOfWeek(studentCourseDays);
  const nCourseDays = courseDaysOfWeek.length;

  // If startDay is not a course day we need to find the next closest course day
  // after startDay
  const findNextCourseDay = (day: Date): Date =>
    courseDaysOfWeek.indexOf(format(day, 'EEEE')) === -1
      ? findNextCourseDay(addDays(day, 1))
      : day;

  // We need a list of unique days that are unavailable (vacations),
  // so we need to filter out overlaps
  const initialUnavailableDays =
    dayStatus === DayStatus.Available
      ? vacationDays
          .filter((day) => courseDaysOfWeek.indexOf(format(day, 'EEEE')) !== -1)
          .map(removeTimeFromDate)
          .map(getTime)
          .filter((day, i, days) => days.indexOf(day) === i)
          .map((day) => new Date(day))
      : [];

  // Each jump lands on a course day, then jumps again by however many unavailable
  // days were skipped over, until a jump skips none
  const jumpPastUnavailableDays = (
    jumpStartDay: Date,
    daysToJump: number,
    unavailableDays: Date[]
  ): Date => {
    const startCourseDayIndex = courseDaysOfWeek.indexOf(
      format(jumpStartDay, 'EEEE')
    );
    const endCourseDayIndex = lookingForward
      ? (startCourseDayIndex + daysToJump) % nCourseDays
      : (nCourseDays +
          ((startCourseDayIndex - Math.abs(daysToJump)) % nCourseDays)) %
        nCourseDays;

    const endDayOfWeekIndex = Math.max(
      0,
      dateFnsWeekDayOrder.indexOf(courseDaysOfWeek[endCourseDayIndex])
    ) as Day;

    const weeksToJump = Math.ceil(Math.abs(daysToJump) / nCourseDays);

    const endDay = jumpWeeks(
      jumpStartDay,
      endDayOfWeekIndex,
      weeksToJump,
      lookingForward
    );

    const currentInterval = lookingForward
      ? { start: jumpStartDay, end: endDay }
      : { start: endDay, end: jumpStartDay };
    const unavailableDaysInInterval = unavailableDays.filter((day) =>
      isWithinInterval(day, currentInterval)
    );

    if (unavailableDaysInInterval.length === 0) {
      return endDay;
    }

    // Remove all used unavailable days so that we don't double count them
    // on the next jump
    return jumpPastUnavailableDays(
      endDay,
      unavailableDaysInInterval.length,
      unavailableDays.filter((day) => !unavailableDaysInInterval.includes(day))
    );
  };

  return jumpPastUnavailableDays(
    findNextCourseDay(startDay),
    countStartDay && lookingForward ? nDaysAhead - 1 : nDaysAhead,
    initialUnavailableDays
  );
}

/**
 * Return assignment start and end dates given course and assignment schedule.
 *
 * @param studentCourseStartDate {Date} date that the course should start
 * @param studentCourseDays {Record<string, boolean>} days of week on which student assignments may be assigned
 * @param studentVacations {DateRangeWithIgnoredCourses[]} list of vacations on which assignments should not be scheduled
 * @param assignmentDay {number | null} course day number that the assignment is scheduled (1-indexed)
 * @param assignmentWeek {number | null} course week number that the assignment is scheduled (1-indexed)
 * @param assignmentDuration {number} number of calendar days until the assignment is due. '0' means the same day
 * @param studentAssignmentCourseDayOffset {number} number of course days before or after the parent assignment's day/week
 * @param studentCourseCourseDayOffset {number} number of course days to adjust the parent assignment's day/week
 * @param courseId {string} the course ID to check against vacation blocks
 * @return {StudentAssignmentDates} an object containing a start date and end date for the new Student Assignment
 */
export function getStudentAssignmentDates(
  studentCourseStartDate: Date,
  studentCourseDays: Record<string, boolean>,
  studentVacations: DateRangeWithIgnoredCourses[],
  assignmentDay: number | null,
  assignmentWeek: number | null,
  assignmentDuration: number,
  courseId: string = '',
  studentAssignmentCourseDayOffset: number = 0,
  studentCourseCourseDayOffset: number = 0
): StudentAssignmentDates {
  if (!assignmentDay || !assignmentWeek) {
    return { start: null, end: null };
  }

  const blockingVacations = courseId
    ? studentVacations.filter(
        (vacation) => !vacation.ignoredCourses.includes(courseId)
      )
    : studentVacations;

  const vacationDays = blockingVacations.flatMap((vacation) =>
    eachDayOfInterval({ start: vacation.from, end: vacation.to })
  );

  const numCourseDaysPerWeek = getValidDaysOfWeek(studentCourseDays).length;
  const assignmentCourseDayNum = getNumDayInCourse(
    numCourseDaysPerWeek,
    assignmentWeek,
    assignmentDay
  );

  const startDayNoOffset = findNthDay(
    assignmentCourseDayNum,
    DayStatus.Available,
    studentCourseStartDate,
    studentCourseDays,
    vacationDays,
    true
  );

  const combinedOffset =
    studentAssignmentCourseDayOffset + studentCourseCourseDayOffset;
  const startDay = combinedOffset
    ? findNthDay(
        combinedOffset,
        DayStatus.Available,
        startDayNoOffset,
        studentCourseDays,
        vacationDays,
        /*countStartDay === */ false
      )
    : startDayNoOffset;

  const endDay = addDays(startDay, assignmentDuration);

  return { start: startDay, end: endDay };
}

interface StudentCourseForProjection {
  start: string;
  daysOfTheWeek: Record<string, boolean> | object;
  courseDayOffsets?: Record<number, number> | object | null;
}

/**
 * Calculates the projected start and end dates for a new student assignment.
 * This is used when previewing where an assignment will land on a student's calendar.
 *
 * @param studentCourse Student course with start date, days of week, and course day offsets
 * @param vacations List of vacation ranges for the student
 * @param assignmentDay Day number in the course week (1-indexed)
 * @param assignmentWeek Week number in the course (1-indexed)
 * @param assignmentDuration Number of calendar days until the assignment is due
 * @param courseId The course ID to check against vacation blocks
 * @returns Object containing projected start and end dates
 */
export function getProjectedStudentAssignmentDates(
  studentCourse: StudentCourseForProjection,
  vacations: DateRangeWithIgnoredCourses[],
  assignmentDay: number | null,
  assignmentWeek: number | null,
  assignmentDuration: number,
  courseId: string
): { start: Date | null; end: Date | null } {
  if (!assignmentDay || !assignmentWeek) {
    return { start: null, end: null };
  }

  const studentCourseDays = studentCourse.daysOfTheWeek as Record<
    string,
    boolean
  >;
  const studentCourseDayOffsets = (studentCourse.courseDayOffsets ??
    {}) as Record<number, number>;

  const courseDayOffset = calcTotalStudentCourseDayOffset(
    assignmentWeek,
    assignmentDay,
    getNumCourseDaysPerWeekFromStudentCourseDays(studentCourseDays),
    studentCourseDayOffsets
  );

  return getStudentAssignmentDates(
    parseISO(studentCourse.start),
    studentCourseDays,
    vacations,
    assignmentDay,
    assignmentWeek,
    assignmentDuration,
    courseId,
    0, // New assignments start with no individual reschedule offset
    courseDayOffset
  );
}

/**
 * Return a new student assignment given an assignment.
 *
 * @param assignmentId {string} course assignment id
 * @param assignmentDay {number | null} course assignment day
 * @param assignmentWeek {number | null} course assignment week
 * @param assignmentDuration {number} course assignment duration
 * @param studentId {string} student ID to which the assignment will be assigned
 * @param studentCourseStartDate {Date} date that the course should start. Always a Monday
 * @param studentCourseDays {Record<string, boolean} days of week on which student assignments may be assigned
 * @param studentVacations {DateRangeWithIgnoredCourses[]} vacations for student in student course
 * @param courseId {string} the course ID to check against vacation blocks
 * @return {NewStudentAssignmentForStudentCourse} a student assignment
 */
function createStudentAssignmentForStudentCourse(
  assignmentId: string,
  assignmentDay: number | null,
  assignmentWeek: number | null,
  assignmentDuration: number,
  studentId: string,
  studentCourseStartDate: Date,
  studentCourseDays: Record<string, boolean>,
  studentVacations: DateRangeWithIgnoredCourses[],
  courseId: string
): NewStudentAssignmentForStudentCourse {
  const studentAssignmentDates = getStudentAssignmentDates(
    studentCourseStartDate,
    studentCourseDays,
    studentVacations,
    assignmentDay,
    assignmentWeek,
    assignmentDuration,
    courseId
  );

  return {
    studentId,
    assignmentId: assignmentId,
    end: studentAssignmentDates.end
      ? formatISO(studentAssignmentDates.end, { representation: 'date' })
      : null,
    start: studentAssignmentDates.start
      ? formatISO(studentAssignmentDates.start, {
          representation: 'date',
        })
      : null,
    status: Student_Assignment_Status_Enum.Incomplete,
  };
}

/**
 * Return a list of student assignments given a list of assignments. Only creates student assignments
 * when the assignment has a day and week.
 *
 * @param assignments {Pick<Assignments, 'id' | 'day' | 'week' | 'duration'>[]} course assignments
 * @param studentId {string} student ID to which the assignment will be assigned
 * @param studentCourseStartDate {Date} course start date
 * @param studentCourseDays {Record<string, boolean} days of week on which student assignments may be assigned
 * @param studentVacations {DateRangeWithIgnoredCourses[]} list of vacations around which to avoid scheduling student assignments
 * @param courseId {string} the course ID to check against vacation blocks
 * @return {NewStudentAssignmentForStudentCourse[] | null} an array of student assignments on success or null upon failure
 */
export function createStudentAssignmentsForStudentCourse(
  assignments: Pick<Assignments, 'id' | 'day' | 'week' | 'duration'>[],
  studentId: string,
  studentCourseStartDate: Date,
  studentCourseDays: Record<string, boolean>,
  studentVacations: DateRangeWithIgnoredCourses[] = [],
  courseId: string
): NewStudentAssignmentForStudentCourse[] {
  if (!assignments.length) {
    return [];
  }

  return assignments.reduce((assignments, assignment) => {
    assignments.push(
      createStudentAssignmentForStudentCourse(
        assignment.id,
        assignment?.day ?? null,
        assignment?.week ?? null,
        assignment.duration,
        studentId,
        studentCourseStartDate,
        studentCourseDays,
        studentVacations,
        courseId
      )
    );

    return assignments;
  }, [] as NewStudentAssignmentForStudentCourse[]);
}

/**
 * Creates a student assignment for an assignment and the provided student course
 *
 * @param studentCourse {StudentCourse} student course for which to create a student assignment
 * @param studentVacations {DateRangeWithIgnoredCourses[]} vacations for the student in studentCourse
 * @param assignmentDay {number | null} the day the source assignment is scheduled for if null
 * the student assignment start and end will be null
 * @param assignmentWeek {number | null} the week the source assignment is scheduled for if null
 * the student assignment start and end will be null
 * @param assignmentDuration {number} the duration of the source assignment
 * @param calendarStartDate {Date | null} optional calendar start date that overrides the calculated start
 * @param calendarEndDate {Date | null} optional calendar end date that overrides the calculated end
 * @param isFreeFormCalendarDate {boolean} if true, marks the student assignment as free-form
 * @return {NewStudentAssignmentForAssignment | null} a student assignment on success or null upon failure
 */
function createStudentAssignmentForAssignment(
  studentCourse: StudentCourse,
  studentVacations: DateRangeWithIgnoredCourses[],
  assignmentDay: number | null,
  assignmentWeek: number | null,
  assignmentDuration: number,
  calendarStartDate: Date | null = null,
  calendarEndDate: Date | null = null,
  isFreeFormCalendarDate: boolean = false
): NewStudentAssignmentForAssignment {
  const baseStudentAssignment = {
    studentId: studentCourse.student.id,
    studentCourseId: studentCourse.id,
    status: Student_Assignment_Status_Enum.Incomplete,
    freeForm: isFreeFormCalendarDate,
  };

  if (calendarStartDate) {
    return {
      start: formatISO(calendarStartDate, { representation: 'date' }),
      end: calendarEndDate
        ? formatISO(calendarEndDate, { representation: 'date' })
        : formatISO(calendarStartDate, { representation: 'date' }),
      ...baseStudentAssignment,
    };
  }

  const courseDayOffset =
    assignmentWeek && assignmentDay
      ? calcTotalStudentCourseDayOffset(
          assignmentWeek,
          assignmentDay,
          getNumCourseDaysPerWeekFromStudentCourseDays(
            studentCourse.daysOfTheWeek as Record<string, boolean>
          ),
          (studentCourse.courseDayOffsets ?? {}) as Record<number, number>
        )
      : 0;

  const studentCourseStartDate = parseISO(studentCourse.start);
  const studentAssignmentDates = getStudentAssignmentDates(
    studentCourseStartDate,
    studentCourse.daysOfTheWeek as Record<string, boolean>,
    studentVacations,
    assignmentDay,
    assignmentWeek,
    assignmentDuration,
    studentCourse.course?.id ?? '',
    courseDayOffset
  );

  return {
    end: studentAssignmentDates.end
      ? formatISO(studentAssignmentDates.end, { representation: 'date' })
      : null,
    start: studentAssignmentDates.start
      ? formatISO(studentAssignmentDates.start, {
          representation: 'date',
        })
      : null,
    ...baseStudentAssignment,
  };
}

/**
 * Creates student assignments for an assignment and the provided student courses
 *
 * This is used when a new assignment gets created and needs to be turned into a student
 * assignment for each student enrolled in the course
 *
 * @param studentCourses {StudentCourse[]} student courses for which to create student assignments - indexes aligned with studentVacations
 * @param studentVacations {DateRangeWithIgnoredCourses[]} vacations for the students in studentCourses indexes aligned with studentCourses
 * @param assignmentDay {number | null} the day the source assignment is scheduled for
 * @param assignmentWeek {number | null} the week the source assignment is scheduled for
 * @param assignmentDuration {number} the duration of the source assignment
 * @param calendarStartDate {Date | null} optional calendar start date that overrides the calculated start
 * @param calendarEndDate {Date | null} optional calendar end date that overrides the calculated end
 * @param isFreeFormCalendarDate {boolean} if true, marks all student assignments as free-form
 * @return {NewStudentAssignmentForAssignment[] | null} an array of student assignments on success or null upon failure
 */
export function createStudentAssignmentsForAssignment(
  studentCourses: StudentCourse[],
  studentVacations: DateRangeWithIgnoredCourses[][],
  assignmentDay: number | null,
  assignmentWeek: number | null,
  assignmentDuration: number,
  calendarStartDate: Date | null = null,
  calendarEndDate: Date | null = null,
  isFreeFormCalendarDate: boolean = false
): NewStudentAssignmentForAssignment[] | null {
  if (studentCourses.length !== studentVacations.length) {
    return null;
  }

  return studentCourses.map((studentCourse, index) =>
    createStudentAssignmentForAssignment(
      studentCourse,
      studentVacations[index],
      assignmentDay,
      assignmentWeek,
      assignmentDuration,
      calendarStartDate,
      calendarEndDate,
      isFreeFormCalendarDate
    )
  );
}

/**
 * Updates the end date of an existing student assignment based on a duration
 *
 * @param studentAssignments {StudentAssignment[]} student assignments with start and end date
 * @param assignmentDuration {number} the duration of the source assignment
 * @return {StudentAssignment[]} an array of student assignments with updated end date
 */
export function updateStudentAssignmentsEnd(
  studentAssignments: StudentAssignment[],
  assignmentDuration: number
): StudentAssignment[] {
  return studentAssignments.map((studentAssignment) => {
    return {
      ...studentAssignment,
      end: studentAssignment.start
        ? formatISO(
            addDays(parseISO(studentAssignment.start), assignmentDuration),
            { representation: 'date' }
          )
        : null,
    };
  });
}

/**
 * Places a student assignment on the correct dates based on vacations and its week/day/duration
 *
 * Note, this function will not reschedule student assignments that are free-form or completed.
 *
 * @param studentCourseStartDate {Date} start day of the student course that the assignment is being reschedule for
 * @param studentAssignments {StudentAssignmentForReschedule} student assignment to reschedule
 * @param studentCourseDays {Record<string, boolean>} days of week on which student assignments may be assigned
 * @param vacations {DateRangeWithIgnoredCourses[]} list of existing vacations for the relevant student
 * @param day {number | null} new day to use for rescheduling student assignment
 * @param week {number | null} new week to use for rescheduling student assignment
 * @param duration {number} new duration to use for rescheduling student assignment
 * @param studentCourseDayOffsets {Record<number,number>} course day offsets for student course
 * @return {StudentAssignmentDates[]} list of rescheduled student assignments
 */
export function rescheduleStudentAssignment(
  studentCourseStartDate: Date,
  studentAssignment: StudentAssignmentForReschedule,
  studentCourseDays: Record<string, boolean>,
  vacations: DateRangeWithIgnoredCourses[],
  day: number | null,
  week: number | null,
  duration: number,
  studentCourseDayOffsets: Record<number, number>
): StudentAssignmentForReschedule {
  if (
    studentAssignment.freeForm ||
    studentAssignment.status === Student_Assignment_Status_Enum.Complete
  ) {
    return studentAssignment;
  }

  const studentCourseDayOffset =
    week && day
      ? calcTotalStudentCourseDayOffset(
          week,
          day,
          getNumCourseDaysPerWeekFromStudentCourseDays(studentCourseDays),
          studentCourseDayOffsets
        )
      : 0;

  const newStudentAssignmentDates = getStudentAssignmentDates(
    studentCourseStartDate,
    studentCourseDays,
    vacations,
    day,
    week,
    duration,
    studentAssignment.assignment.course.id,
    studentAssignment.courseDayOffset || 0,
    studentCourseDayOffset
  );

  return {
    ...studentAssignment,
    start: newStudentAssignmentDates.start
      ? formatISO(newStudentAssignmentDates.start, {
          representation: 'date',
        })
      : null,
    end: newStudentAssignmentDates.end
      ? formatISO(newStudentAssignmentDates.end, {
          representation: 'date',
        })
      : null,
  };
}

/**
 * Return the provided student assignment with an updated courseDayOffset, or the original student assignment if unsuccessful
 *
 * The updated courseDayOffset is calculated such that the student assignment's start/end dates would remain the same after a reschedule.
 * I.e., this function "fixes" the student assignment's offset to match its current location in the course calendar.
 *
 * Note, this function will not reoffset student assignments that are free-form or completed.
 *
 * @param studentCourseStartDate start day of the student course that the assignment belongs to
 * @param studentAssignment student assignment to reoffset
 * @param studentCourseDays boolean>} days of week on which student assignments may be assigned
 * @param vacations list of existing vacations for the relevant student
 * @param studentCourseDayOffsets {Record<number,number>} course day offsets for student course
 * @return student assignment with updated offset, or the original student assignment if unsuccessful
 */
export function reoffsetStudentAssignment(
  studentCourseStartDate: Date,
  studentAssignment: StudentAssignmentForRescheduleUsingAssignment,
  studentCourseDays: Record<string, boolean>,
  vacations: DateRangeWithIgnoredCourses[],
  studentCourseDayOffsets: Record<number, number>
): StudentAssignmentForReschedule {
  const currentStartDate = studentAssignment.start;
  if (
    !currentStartDate ||
    !studentAssignment.assignment.day ||
    !studentAssignment.assignment.week
  ) {
    // TODO: log?
    return studentAssignment;
  }

  const scheduledCourseDay = studentAssignment.assignment.day;
  const scheduledCourseWeek = studentAssignment.assignment.week;

  const stateDateIfRescheduled = rescheduleStudentAssignment(
    studentCourseStartDate,
    studentAssignment,
    studentCourseDays,
    vacations,
    scheduledCourseDay,
    scheduledCourseWeek,
    0,
    studentCourseDayOffsets
  ).start;

  if (!stateDateIfRescheduled) {
    // TODO: log?
    return studentAssignment;
  }

  const numCourseDaysOff = getDayStatusCounts(
    { from: parseISO(currentStartDate), to: parseISO(stateDateIfRescheduled) },
    studentCourseDays,
    vacations,
    studentAssignment.assignment.course.id
  )[DayStatus.Available];

  const courseDayOffsetAdjustment = calcOffsetFromDayCount(numCourseDaysOff);

  return {
    ...studentAssignment,
    courseDayOffset:
      (studentAssignment.courseDayOffset ?? 0) - courseDayOffsetAdjustment,
  };
}

/**
 * Return a list of student assignments with updated courseDayOffsets
 *
 * @param studentCourseStartDate start day of the student course
 * @param studentAssignments student assignments to reoffset
 * @param studentCourseDays days of the week on which student assignments may be assigned
 * @param vacations list of existing vacations for the student
 * @param studentCourseDayOffsets course day offsets for the student course
 * @returns
 */
export function reoffsetStudentAssignments(
  studentCourseStartDate: Date,
  studentAssignments: StudentAssignmentForRescheduleUsingAssignment[],
  studentCourseDays: Record<string, boolean>,
  vacations: DateRangeWithIgnoredCourses[],
  studentCourseDayOffsets: Record<number, number>
): StudentAssignmentForReschedule[] {
  const offsetAdjustedStudentAssignments = studentAssignments.map(
    (studentAssignment) =>
      reoffsetStudentAssignment(
        studentCourseStartDate,
        studentAssignment,
        studentCourseDays,
        vacations,
        studentCourseDayOffsets
      )
  );

  return offsetAdjustedStudentAssignments;
}

/**
 * Places each student assignment on the correct dates based on vacations and its week/day/duration
 *
 * Note, this function will not reschedule student assignments that are free-form.
 *
 * @param studentCourseStartDate {Date} start day of the student course that assignments are being reschedule for
 * @param studentAssignments {StudentAssignmentForRescheduleUsingAssignment[]} student assignments to reschedule
 * @param studentCourseDays {Record<string, boolean>} days of week on which student assignments may be assigned
 * @param vacations {DateRangeWithIgnoredCourses[]} list of existing vacations
 * @param studentCourseDayOffsets {Record<number,number>} course day offsets for student course
 * @return {StudentAssignmentForReschedule[]} list of rescheduled student assignments
 */
export function rescheduleStudentAssignments(
  studentCourseStartDate: Date,
  studentAssignments: StudentAssignmentForRescheduleUsingAssignment[],
  studentCourseDays: Record<string, boolean>,
  vacations: DateRangeWithIgnoredCourses[],
  studentCourseDayOffsets: Record<number, number>
): StudentAssignmentForReschedule[] {
  const rescheduledStudentAssignments = studentAssignments.map(
    (studentAssignment) =>
      rescheduleStudentAssignment(
        studentCourseStartDate,
        studentAssignment,
        studentCourseDays,
        vacations,
        studentAssignment.assignment.day ?? null,
        studentAssignment.assignment.week ?? null,
        studentAssignment.assignment.duration,
        studentCourseDayOffsets
      )
  );

  return rescheduledStudentAssignments;
}

function getPercentPointsEarned({
  pointsEarned: originalPointsEarned,
  pointsAvailable: originalPointsAvailable,
}: StudentAssignmentForUpdatingGradingStyle): number | null {
  if (originalPointsEarned === undefined || originalPointsEarned === null) {
    return null;
  }
  if (
    originalPointsAvailable === undefined ||
    originalPointsAvailable === null
  ) {
    return originalPointsEarned; // Not sure how this would happen
  }
  if (originalPointsEarned === 0 && originalPointsAvailable === 0) {
    return null; // A 0/0 assignment
  }
  if (originalPointsEarned > 0 && originalPointsAvailable === 0) {
    return 100; // An Extra Credit assignment
  }
  if (originalPointsEarned === 0) {
    return 0; // A 0/X assignment
  }
  return Number(
    (100 * (originalPointsEarned / originalPointsAvailable)).toFixed(1)
  );
}

export function convertToPercentGradingStyle(
  studentAssignments: StudentAssignmentForUpdatingGradingStyle[]
): StudentAssignmentForUpdatingGradingStyle[] {
  return studentAssignments.map((studentAssignment) => ({
    ...studentAssignment,
    pointsEarned: getPercentPointsEarned(studentAssignment),
    pointsAvailable: 100,
  }));
}

/**
 * Calculates the calendar dates for all week/day combinations for all enrolled students.
 * Returns a map keyed by "week-day" with an array of student date info for each.
 *
 * @param studentCourses Array of student courses with student info and schedules
 * @param studentVacations Array of vacation arrays aligned with studentCourses
 * @param numberOfWeeks Total weeks in the course
 * @param numberOfDaysPerWeek Days per week in the course
 * @param courseId The course ID for vacation filtering
 * @returns StudentDatesMap keyed by "week-day"
 */
export function calculateAllStudentDatesForCourse(
  studentCourses: StudentCourseForDateCalc[],
  studentVacations: DateRangeWithIgnoredCourses[][],
  numberOfWeeks: number,
  numberOfDaysPerWeek: number,
  courseId: string
): StudentDatesMap {
  const weeks = Array.from({ length: numberOfWeeks }, (_, i) => i + 1);
  const days = Array.from({ length: numberOfDaysPerWeek }, (_, i) => i + 1);

  return weeks
    .flatMap((week) => days.map((day) => ({ week, day })))
    .reduce((result, { week, day }) => {
      const key = `${week}-${day}`;

      result[key] = studentCourses.map((studentCourse, index) => {
        const vacations = studentVacations[index] ?? [];

        const { start: dateForDay } = getProjectedStudentAssignmentDates(
          studentCourse,
          vacations,
          day,
          week,
          0,
          courseId
        );

        return {
          studentId: studentCourse.student.id,
          studentName: studentCourse.student.name,
          date: dateForDay,
        };
      });

      return result;
    }, {} as StudentDatesMap);
}

/**
 * Formats student date information for tooltip display.
 * - If 1-4 students: Shows individually (e.g., "Andrew - 01/15/2026")
 * - If >4 students: Groups students with same date (e.g., "3 Students - 01/15/2026")
 *
 * @param studentDates Array of student date info for a specific week/day
 * @param dateFormat Optional date format string (default: 'MM/dd/yyyy')
 * @returns Array of formatted strings for tooltip display
 */
export function formatStudentDatesTooltip(
  studentDates: StudentDateInfo[],
  dateFormat: string = 'MM/dd/yyyy'
): string[] {
  const validDates = studentDates.filter(
    (sd): sd is StudentDateInfo & { date: Date } => sd.date !== null
  );

  if (validDates.length === 0) {
    return [];
  }

  if (validDates.length <= MAX_STUDENTS_FOR_INDIVIDUAL_DISPLAY) {
    return validDates.map(
      (sd) => `${sd.studentName} - ${format(sd.date, dateFormat)}`
    );
  }

  const dateGroups: Record<string, string[]> = {};
  validDates.forEach((sd) => {
    const dateKey = format(sd.date, dateFormat);
    if (!dateGroups[dateKey]) {
      dateGroups[dateKey] = [];
    }
    dateGroups[dateKey].push(sd.studentName);
  });

  return Object.entries(dateGroups).map(([date, names]) => {
    if (names.length === 1) {
      return `${names[0]} - ${date}`;
    }
    return `${names.length} Students - ${date}`;
  });
}

/**
 * Formats a student assignment's scheduled date range for display.
 *
 * @param startDate ISO date string for the start date
 * @param endDate ISO date string for the end date
 * @param useShortFormat Whether to use short format (M/d) or long format (E, M/d)
 * @returns Formatted date string, or 'Unscheduled' if dates are missing
 */
export function formatScheduledDate(
  startDate?: string | null,
  endDate?: string | null,
  useShortFormat = false
): string {
  if (!startDate || !endDate) {
    return 'Unscheduled';
  }

  const start = parseISO(startDate);
  const end = parseISO(endDate);

  if (isSameDay(start, end)) {
    return useShortFormat ? format(end, 'M/d') : format(end, 'E, M/d');
  }

  return useShortFormat
    ? `${format(start, 'M/d')} - ${format(end, 'M/d')}`
    : `${format(start, 'E, M/d')} - ${format(end, 'E, M/d')}`;
}

export function getEffectiveDisplayOrder(
  assignment: {
    start?: string | null;
    end?: string | null;
    displayOrder?: number | null;
    endDisplayOrder?: number | null;
  },
  dayStr: string
): number | null {
  const isEndDay = assignment.end === dayStr && assignment.start !== dayStr;
  return isEndDay
    ? assignment.endDisplayOrder ?? null
    : assignment.displayOrder ?? null;
}

export function compareByDisplayOrder(
  a: {
    start?: string | null;
    end?: string | null;
    displayOrder?: number | null;
    endDisplayOrder?: number | null;
  },
  b: {
    start?: string | null;
    end?: string | null;
    displayOrder?: number | null;
    endDisplayOrder?: number | null;
  },
  dayStr: string
): number {
  const aOrder = getEffectiveDisplayOrder(a, dayStr);
  const bOrder = getEffectiveDisplayOrder(b, dayStr);

  if (aOrder !== null && bOrder !== null) {
    return aOrder - bOrder;
  }
  if (aOrder !== null) {
    return -1;
  }
  if (bOrder !== null) {
    return 1;
  }
  return 0;
}

interface AssignmentForPartition {
  id: string;
  courseDayOffset?: number | null;
  assignment: { week?: number | null; day?: number | null };
}

export interface PartitionedAssignments<T extends AssignmentForPartition> {
  toReschedule: T[];
  toKeepInPlace: T[];
}

/**
 * Splits fetched assignments into two groups for a shift reschedule:
 * - toReschedule: selected assignments, assignments at non-shifted positions,
 *   and assignments with an existing courseDayOffset (previously individually rescheduled)
 * - toKeepInPlace: unselected assignments at shifted positions with no individual offset
 *
 * Every input assignment ends up in exactly one of the two output arrays.
 *
 * @param assignments all fetched student assignments for the course being shifted
 * @param selectedAssignmentIds IDs of the assignments being shifted
 * @param shiftedPositions week/day positions that are being shifted
 * @param numCourseDaysPerWeek number of course days per week for dayInCourse calculation
 * @returns an object with toReschedule and toKeepInPlace arrays
 */
export function partitionAssignmentsForShift<T extends AssignmentForPartition>(
  assignments: T[],
  selectedAssignmentIds: string[],
  shiftedPositions: { week: number; day: number }[],
  numCourseDaysPerWeek: number
): PartitionedAssignments<T> {
  const shiftedDayInCoursePositions = new Set(
    shiftedPositions.map((position) =>
      getNumDayInCourse(numCourseDaysPerWeek, position.week, position.day)
    )
  );
  const selectedIdSet = new Set(selectedAssignmentIds);

  const toReschedule: T[] = [];
  const toKeepInPlace: T[] = [];

  assignments.forEach((studentAssignment) => {
    const dayInCourse = getNumDayInCourse(
      numCourseDaysPerWeek,
      studentAssignment.assignment.week,
      studentAssignment.assignment.day
    );

    const shouldKeepInPlace =
      !selectedIdSet.has(studentAssignment.id) &&
      shiftedDayInCoursePositions.has(dayInCourse) &&
      !studentAssignment.courseDayOffset;

    if (shouldKeepInPlace) {
      toKeepInPlace.push(studentAssignment);
    } else {
      toReschedule.push(studentAssignment);
    }
  });

  return { toReschedule, toKeepInPlace };
}

/**
 * Returns the assignment with the earliest position in the course
 * schedule (lowest week first, then lowest day as tiebreaker).
 * When multiple assignments share the same position, the first one
 * in the input array is returned.
 *
 * @param assignments non-empty array of assignments with week/day positions
 * @returns the assignment with the earliest course position
 */
export function findEarliestAssignment<
  T extends { assignmentWeek: number; assignmentDay: number }
>(assignments: T[]): T {
  return assignments.reduce((earliest, assignment) => {
    if (assignment.assignmentWeek < earliest.assignmentWeek) {
      return assignment;
    }
    if (
      assignment.assignmentWeek === earliest.assignmentWeek &&
      assignment.assignmentDay < earliest.assignmentDay
    ) {
      return assignment;
    }
    return earliest;
  });
}

type AssignmentForCollisionCheck = {
  assignment: {
    week?: number | null | undefined;
    day?: number | null | undefined;
  };
  start?: string | null | undefined;
  courseDayOffset?: number | null | undefined;
};

/**
 * Finds the earliest structural position among assignments that would
 * collide with the shift target date. An assignment collides when it
 * is at an earlier position than the shift, has a non-zero individual
 * courseDayOffset, and its calendar date is at or after the target.
 *
 * @param shiftDayInCourse the course day number of the assignment being shifted
 * @param targetDateISO the target calendar date as an ISO string (YYYY-MM-DD)
 * @param assignments the assignments from the reschedule query
 * @param numCourseDaysPerWeek number of course days per week
 * @returns the earliest colliding position, or shiftDayInCourse if none
 */
export function findEffectiveShiftPosition(
  shiftDayInCourse: number,
  targetDateISO: string,
  assignments: AssignmentForCollisionCheck[],
  numCourseDaysPerWeek: number
): number {
  return assignments.reduce((earliestPosition, assignment) => {
    if (!assignment.assignment.week || !assignment.assignment.day) {
      return earliestPosition;
    }

    const dayInCourse = getNumDayInCourse(
      numCourseDaysPerWeek,
      assignment.assignment.week,
      assignment.assignment.day
    );

    if (
      dayInCourse < shiftDayInCourse &&
      assignment.courseDayOffset != null &&
      assignment.courseDayOffset !== 0 &&
      assignment.start != null &&
      assignment.start >= targetDateISO
    ) {
      return Math.min(earliestPosition, dayInCourse);
    }

    return earliestPosition;
  }, shiftDayInCourse);
}

/**
 * Adjusts shift reschedule offsets to avoid date collisions with
 * individually-rescheduled assignments at earlier structural positions.
 *
 * When a course shift places an offset at position P, earlier-position
 * assignments with individual offsets that land on/after the target date
 * won't be pushed forward (since the cumulative sum only reaches P+).
 * This function detects such collisions and moves the offset to the
 * earliest colliding position so the cumulative sum pushes those
 * assignments forward naturally.
 *
 * @param newOffsets the computed shift offsets (from calcShiftRescheduleOffsets or manual)
 * @param originalOffsets the existing course-level offsets before the shift
 * @param targetDateISO the target calendar date as an ISO string (YYYY-MM-DD)
 * @param assignments the assignments from the reschedule query
 * @param numCourseDaysPerWeek number of course days per week
 * @returns adjusted offsets with collision-safe positioning
 */
export function adjustShiftOffsetsForCollisions(
  newOffsets: Record<number, number>,
  originalOffsets: Record<number, number>,
  targetDateISO: string,
  assignments: AssignmentForCollisionCheck[],
  numCourseDaysPerWeek: number
): Record<number, number> {
  if (Object.keys(newOffsets).length === 0) {
    return newOffsets;
  }

  const earliestShiftPosition = Math.min(
    ...Object.keys(newOffsets).map(Number)
  );

  const effectivePosition = findEffectiveShiftPosition(
    earliestShiftPosition,
    targetDateISO,
    assignments,
    numCourseDaysPerWeek
  );

  if (effectivePosition >= earliestShiftPosition) {
    return newOffsets;
  }

  const delta =
    (newOffsets[earliestShiftPosition] ?? 0) -
    (originalOffsets[earliestShiftPosition] ?? 0);

  return Object.fromEntries([
    ...Object.entries(newOffsets).filter(
      ([key]) => Number(key) !== earliestShiftPosition
    ),
    [effectivePosition, (originalOffsets[effectivePosition] ?? 0) + delta],
  ]);
}
